/**
 * Shared HTTP client for all services
 * This replaces the missing apiService export from api.ts
 */

import { ApiResponse } from '@/types';

class HttpClient {
  private baseUrl: string;

  constructor() {
    this.baseUrl = import.meta.env.VITE_API_BASE_URL || '/api/v1';
  }

  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    const token = localStorage.getItem('auth_token');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  async request<T>(url: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${this.baseUrl}${url}`, {
        ...options,
        headers: {
          ...this.getHeaders(),
          ...options.headers,
        },
        credentials: 'include',
      });

      const result = await response.json();

      if (!response.ok) {
        return {
          success: false,
          message: result.message || `HTTP ${response.status}`,
          errors: result.errors,
        };
      }

      return {
        success: result.success !== undefined ? result.success : true,
        data: result.data || result,
        message: result.message,
      };
    } catch (error: any) {
      console.error('HTTP Request Error:', error);
      return {
        success: false,
        message: error.message || 'Network error',
      };
    }
  }

  async get<T>(url: string, params?: Record<string, any>): Promise<ApiResponse<T>> {
    const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
    return this.request<T>(`${url}${queryString}`, {
      method: 'GET',
    });
  }

  async post<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(url, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(url, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async patch<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(url, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(url: string): Promise<ApiResponse<T>> {
    return this.request<T>(url, {
      method: 'DELETE',
    });
  }

  async upload<T>(
    url: string,
    formData: FormData,
    onProgress?: (progress: number) => void
  ): Promise<ApiResponse<T>> {
    return new Promise(resolve => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${this.baseUrl}${url}`);

      const token = localStorage.getItem('auth_token');
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }

      if (onProgress) {
        xhr.upload.onprogress = event => {
          if (event.lengthComputable) {
            const progress = Math.round((event.loaded / event.total) * 100);
            onProgress(progress);
          }
        };
      }

      xhr.onload = () => {
        try {
          const result = JSON.parse(xhr.responseText);
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve({
              success: result.success !== undefined ? result.success : true,
              data: result.data || result,
              message: result.message,
            });
          } else {
            resolve({
              success: false,
              message: result.message || `HTTP ${xhr.status}`,
              errors: result.errors,
            });
          }
        } catch {
          resolve({
            success: false,
            message: 'Failed to parse response',
          });
        }
      };

      xhr.onerror = () => {
        resolve({
          success: false,
          message: 'Network error',
        });
      };

      xhr.send(formData);
    });
  }
}

// Create a public client without auth headers
class PublicHttpClient {
  private baseUrl: string;

  constructor() {
    this.baseUrl = import.meta.env.VITE_API_BASE_URL || '/api/v1';
  }

  private getHeaders(): HeadersInit {
    return {
      'Content-Type': 'application/json',
    };
  }

  async request<T>(url: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${this.baseUrl}${url}`, {
        ...options,
        headers: {
          ...this.getHeaders(),
          ...options.headers,
        },
        credentials: 'include',
      });

      const result = await response.json();

      if (!response.ok) {
        return {
          success: false,
          message: result.message || `HTTP ${response.status}`,
          errors: result.errors,
        };
      }

      return {
        success: result.success !== undefined ? result.success : true,
        data: result.data || result,
        message: result.message,
      };
    } catch (error: any) {
      console.error('HTTP Request Error:', error);
      return {
        success: false,
        message: error.message || 'Network error',
      };
    }
  }

  async get<T>(url: string, params?: Record<string, any>): Promise<ApiResponse<T>> {
    const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
    return this.request<T>(`${url}${queryString}`, {
      method: 'GET',
    });
  }

  async post<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(url, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }
}

// Export a singleton instance
export const apiService = new HttpClient();

// Export public API instance (no auth headers)
export const publicApi = new PublicHttpClient();

// Also export the class for testing or custom instances
export default HttpClient;
